import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import TextareaAutosize from 'react-autosize-textarea';
import Select from 'react-select';
import { formatCurrencyPlain } from '../../../numberUtils';
import { postRequest, putRequest } from '../../../requestUtils';
import calender from '../../../../assets/images/icons/calender.svg';
import dollar from '../../../../assets/images/icons/dollar.svg';
import './AdHocInvoiceModal.scss';

const validateFormInputs = (invoiceDate, invoiceBy, grossRevenue, consultantPayout, invoiceDescription) => {
  const errors = {};

  const inputs = [
    { name: 'invoiceDate', value: invoiceDate },
    { name: 'invoiceBy', value: invoiceBy },
    { name: 'grossRevenue', value: grossRevenue },
    { name: 'consultantPayout', value: consultantPayout },
    { name: 'invoiceDescription', value: invoiceDescription },
  ];
  inputs.forEach((input) => {
    if (!input.value) {
      errors[input.name] = 'Required';
    }
  });

  return errors;
};

const formData = (
  invoiceDate,
  invoiceBy,
  grossRevenue,
  consultantPayout,
  quickbooksInvoiceId,
  invoiceDescription,
  projectId,
) => ({
  invoice_date: invoiceDate,
  invoice_by: invoiceBy.value,
  gross_revenue: grossRevenue.replace(/,/, '') * 100,
  consultant_payout: consultantPayout.replace(/,/, '') * 100,
  quickbooks_invoice_id: quickbooksInvoiceId,
  invoice_description: invoiceDescription,
  project_id: projectId,
});

const initializeFormState = (invoice = {}) => ({
  showModal: false,
  invoiceDate: invoice?.invoice_date ? new Date(invoice.invoice_date) : null,
  invoiceBy: invoice?.invoice_by || '',
  grossRevenue: formatCurrencyPlain((invoice?.gross_revenue || 0) / 100),
  consultantPayout: formatCurrencyPlain((invoice?.consultant_payout || 0) / 100),
  quickbooksInvoiceId: invoice?.quickbooks_invoice_id || '',
  invoiceDescription: invoice?.invoice_description || '',
  isProcessing: false,
  errors: {},
});

class AdHocInvoiceModal extends Component {
  constructor(props) {
    super(props);
    const { invoice } = props;
    this.state = {
      formMode: props.formMode,
      ...initializeFormState(invoice),
    };

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleOnKeyDown = this.handleOnKeyDown.bind(this);
  }

  open() {
    this.setState({ showModal: true });
  }

  close() {
    const { formMode } = this.props;

    if (formMode === 'create') {
      this.setState({ ...initializeFormState() });
    } else {
      this.setState({ showModal: false });
    }
  }

  handleChange(name, value) {
    const { errors } = this.state;
    const newErrors = { ...errors, [name]: '' };
    this.setState({ [name]: value, errors: newErrors });
  }

  handleCurrentInputFormat(name, value) {
    if (formatCurrencyPlain(value) === 'NaN') {
      this.setState({ [name]: '' });
    } else {
      this.setState({ [name]: formatCurrencyPlain(value) });
    }
  }

  handleSubmit() {
    const {
      invoiceDate, invoiceBy, grossRevenue,
      consultantPayout, quickbooksInvoiceId, invoiceDescription, formMode,
    } = this.state;
    const {
      adHocInvoicesUrl, csrfToken, projectId, invoice,
    } = this.props;

    const errors = validateFormInputs(invoiceDate, invoiceBy, grossRevenue, consultantPayout, invoiceDescription);

    if (Object.keys(errors).length !== 0) {
      this.setState({ errors });
      return;
    }
    const params = formData(
      invoiceDate,
      invoiceBy,
      grossRevenue,
      consultantPayout,
      quickbooksInvoiceId,
      invoiceDescription,
      projectId,
    );
    this.setState({ isProcessing: true, processingError: '' });

    if (formMode === 'create') {
      postRequest(
        adHocInvoicesUrl,
        params,
        csrfToken,
      ).then(() => {
        this.close();
        window.location.reload();
      })
        .catch(() => {
          this.setState({
            isProcessing: false,
            processingError: 'There an error processing your request try again later',
          });
        });
    } else {
      putRequest(
        `${adHocInvoicesUrl}/${invoice.resource_id}`,
        params,
        csrfToken,
      ).then(() => {
        this.close();
        window.location.reload();
      })
        .catch(() => {
          this.setState({
            isProcessing: false,
            processingError: 'There an error processing your request try again later',
          });
        });
    }
  }

  handleDelete() {
    const { handleDelete } = this.props;
    handleDelete();
  }

  handleOnKeyDown(event) {
    if (event.keyCode === 32) {
      event.stopPropagation();
    }
  }

  render() {
    const {
      triggerText, modalHeader, companyName, projectName,
      consultantFullName,
    } = this.props;
    const {
      showModal, invoiceBy, invoiceDate, grossRevenue, formMode, isProcessing,
      consultantPayout, quickbooksInvoiceId, invoiceDescription, errors, processingError,
    } = this.state;

    return (
      <Fragment>
        <Button variant="ad-hoc-trigger-button" onClick={this.open}>
          {triggerText}
        </Button>

        <Modal
          show={showModal}
          id=""
          dialogClassName="ad-hoc-invoice-modal"
          onHide={this.open}
          onEscapeKeyDown={this.open}
          size="lg"
        >
          <Modal.Body>
            <div
              onClick={this.close}
              role="button"
              tabIndex={0}
              className="close-modal"
            >
              <i className="fas fa-times fa-lg" />
            </div>
            <div className="header">
              {modalHeader}
            </div>
            <div className="sub-header">
              Create a new invoice that cannot be billed via timesheet,
              e.g. FTE conversion fee, early termination fee, etc.
            </div>
            <div className="invoice-info">
              <ul className="invoice-info-item">
                <li>
                  Client:
                  <strong>{companyName}</strong>
                </li>
                <li>
                  Project:
                  {' '}
                  <strong>{projectName}</strong>
                </li>
                <li>
                  Consultant Name:
                  <strong>{consultantFullName}</strong>
                </li>
              </ul>
            </div>
            <div className="form-row-two">
              <div className=" me-5">
                <label htmlFor="invoice-date">
                  Invoice Date
                </label>
                <div className="input-with-symbol">
                  <img className="pe-3" src={calender} alt="calender sign" />
                  <DatePicker
                    selected={invoiceDate}
                    onChange={(date) => this.handleChange('invoiceDate', date)}
                    name="invoice-date"
                    placeholderText="mm/dd/yyyy"
                  />
                </div>
                {errors.invoiceDate
                  && <span className="error">{errors.invoiceDate}</span>}
              </div>
              <div className="">
                <label htmlFor="invoice-by">
                  Invoice By
                </label>
                <Select
                  value={invoiceBy}
                  onChange={(event) => this.handleChange('invoiceBy', event)}
                  options={[
                    { value: 'clora', label: 'Clora' },
                    { value: 'consultant', label: 'Consultant' },
                  ]}
                  className="invoice-by"
                  name="invoice-by"
                  id="invoice-by"
                  placeholder="Select one"
                />
                {errors.invoiceBy
                  && <span className="error">{errors.invoiceBy}</span>}
              </div>
            </div>
            <div className="form-row-two">
              <div className="me-5">
                <label htmlFor="gross-revenue">
                  Gross Revenue
                </label>
                <div className="input-with-symbol">
                  <img className="pe-3" src={dollar} alt="dollar sign" />
                  <input
                    type="text"
                    onChange={(event) => this.handleChange('grossRevenue', event.target.value)}
                    onBlur={(event) => this.handleCurrentInputFormat('grossRevenue', event.target.value)}
                    value={grossRevenue}
                    name="gross-revenue"
                    id="gross-revenue"
                    placeholder="99,999.00"
                  />
                </div>
                {errors.grossRevenue
                  && <span className="error">{errors.grossRevenue}</span>}
              </div>
              <div>
                <label htmlFor="consultant-payout">
                  Consultant Payout (excluding non-revenue)
                </label>
                <div className="input-with-symbol">
                  <img className="pe-3" src={dollar} alt="dollar sign" />
                  <input
                    type="text"
                    onChange={(event) => this.handleChange('consultantPayout', event.target.value)}
                    onBlur={(event) => this.handleCurrentInputFormat('consultantPayout', event.target.value)}
                    value={consultantPayout}
                    name="consultant-payout"
                    id="consultant-payout"
                    placeholder="99,999.00"
                  />
                </div>
                {errors.consultantPayout
                  && <span className="error">{errors.consultantPayout}</span>}
              </div>
            </div>
            <div className="form-row-single">
              <label htmlFor="quickbooks-id">
                QuickBooks Invoice ID
              </label>
              <input
                type="text"
                onChange={(event) => this.handleChange('quickbooksInvoiceId', event.target.value)}
                onKeyDown={(event) => this.handleOnKeyDown(event)}
                value={quickbooksInvoiceId}
                placeholder="Enter an invoice ID, if available"
                className="quickbooks-id"
                name="quickbooks-id"
                id="quickbooks-id"
              />
            </div>
            <div className="form-row-single">
              <label htmlFor="invoice-description">
                Invoice Description
              </label>
              <TextareaAutosize
                value={invoiceDescription}
                rows={3}
                maxRows={5}
                onChange={(event) => this.handleChange('invoiceDescription', event.target.value)}
                onKeyDown={(event) => this.handleOnKeyDown(event)}
                className="invoice-description"
                name="invoice-description"
              />
              {errors.invoiceDescription
                && <span className="error">{errors.invoiceDescription}</span>}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="form-actions mb-2">
              {formMode === 'Edit'
                && (
                <button className="btn-delete" onClick={this.handleDelete}>
                  <i className="fa fa-trash-alt mr-3" />
                  Delete Invoice
                </button>
                )}
              <button className="btn-cancel" onClick={this.close}>Cancel</button>
              <button className="btn-save" onClick={this.handleSubmit}>
                {isProcessing ? 'Saving...' : 'Save'}
              </button>
            </div>
            {processingError && <span className="error">{processingError}</span>}
          </Modal.Footer>
        </Modal>
      </Fragment>
    );
  }
}

AdHocInvoiceModal.propTypes = {
  triggerText: PropTypes.string,
  csrfToken: PropTypes.string.isRequired,
  formMode: PropTypes.string,
  modalHeader: PropTypes.string,
  companyName: PropTypes.string.isRequired,
  projectName: PropTypes.string.isRequired,
  consultantFullName: PropTypes.string.isRequired,
  adHocInvoicesUrl: PropTypes.string.isRequired,
  projectId: PropTypes.number.isRequired,
  invoice: PropTypes.shape({}),
  handleDelete: PropTypes.func,
};

AdHocInvoiceModal.defaultProps = {
  triggerText: 'Create Ad-Hoc invoice',
  formMode: 'create',
  modalHeader: 'New Ad-Hoc Invoice',
  handleDelete: () => {},
  invoice: {},
};

export default AdHocInvoiceModal;
